import React, { useState, memo, useCallback } from "react";
import Markdown from "react-markdown";
import { URL_RE, formatCell } from "./executionUtils";

const mdComponents = {
    a: ({ href, children }) => (
        <a href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
            className="text-blue-600 underline hover:text-blue-800 text-[11px]">{children}</a>
    ),
};

const CellContent = memo(function CellContent({ value, expanded }) {
    if (value == null) return "";
    if (typeof value === "string" && URL_RE.test(value.trim())) {
        return (
            <a href={value.trim()} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}
                className="text-blue-600 underline hover:text-blue-800">
                {expanded ? value : value.slice(0, 40) + "\u2026"}
            </a>
        );
    }
    if (expanded && typeof value === "string") {
        return (
            <Markdown components={mdComponents}
                className="prose prose-xs max-w-none text-[11px] leading-tight [&_p]:text-[11px] [&_li]:text-[11px] [&_h1]:text-xs [&_h2]:text-xs [&_h3]:text-[11px]">
                {value}
            </Markdown>
        );
    }
    return formatCell(value);
});

const TableRow = memo(function TableRow({ row, idx, columns, isExpanded, onToggle }) {
    const handleClick = () => { if (!window.getSelection()?.toString()) onToggle(); };
    return (
        <tr onClick={handleClick}
            className={`border-b border-gray-100 cursor-pointer hover:bg-blue-50 ${idx % 2 ? "bg-gray-50" : "bg-white"} ${isExpanded ? "bg-blue-50" : ""}`}>
            {columns.map((col) => (
                <td key={col} className={`px-3 py-1.5 text-gray-700 align-top ${isExpanded ? "break-words" : "truncate max-w-[200px]"}`}>
                    <CellContent value={row[col]} expanded={isExpanded} />
                </td>
            ))}
        </tr>
    );
});

export default function ExpandableTable({ rows, columns }) {
    const [expandedIdx, setExpandedIdx] = useState(null);
    const handleToggle = useCallback((idx) => setExpandedIdx((prev) => prev === idx ? null : idx), []);

    return (
        <div className="border border-gray-200 rounded overflow-hidden overflow-x-auto max-h-[300px] overflow-y-auto">
            <table className="w-full text-[11px]">
                <thead className="bg-gray-50 sticky top-0 z-10">
                    <tr className="border-b border-gray-200">
                        {columns.map((col) => <th key={col} className="text-left px-3 py-1.5 font-medium text-gray-500 min-w-[120px]">{col}</th>)}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row, idx) => (
                        <TableRow key={row.id || row._id || idx} row={row} idx={idx} columns={columns}
                            isExpanded={expandedIdx === idx} onToggle={() => handleToggle(idx)} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}
